import { FC, useCallback, useMemo } from 'react';

import { isEmpty } from 'remeda';
import useSWR from 'swr';

import { ACTION_TYPE_OPTIONS, PROVIDER_OPTIONS, TRANSACTIONS_TTL_DAYS } from './constants';
import { CursorPagination } from './cursorPagination/CursorPagination';
import { DateTimeFilter } from './dateTimeFilter/DateTimeFilter';
import { ProviderTransactionsTable } from './providerTransactionsTable/ProviderTransactionsTable';
import { SlotOption, SlotPicker } from './slotPicker/SlotPicker';
import { TournamentPicker } from './tournamentPicker/TournamentPicker';
import { useProviderTransactionsFilter } from './useProviderTransactionsFilter';
import { UserPicker } from './userPicker/UserPicker';
import { handleProviderTransactionsError } from './utils';

import css from './ProviderTransactionsPage.module.css';

import { providerTransactionService } from 'v2/api/providerTransactionService';
import { EmptyPlaceholder } from 'v2/components/emptyPlaceholder/EmptyPlaceholder';
import { Loader } from 'v2/components/loader/Loader';
import { PageLayout } from 'v2/components/pageLayout/PageLayout';
import { PageTitle } from 'v2/components/pageLayout/pageTitle/PageTitle';
import { Select } from 'v2/components/select/Select';
import { ProviderName } from 'v2/constants';
import { useCursorPagination } from 'v2/hooks/useCursorPagination';
import { Nullable } from 'v2/types';
import { ProviderTransactionFilter } from 'v2/types/ProviderTransactionModel';

export const ProviderTransactionsPage: FC = () => {
  const { filter, requestFilter, limit, onChange, onPatch, onLimitChange } = useProviderTransactionsFilter();

  const resetKey = useMemo(() => JSON.stringify([requestFilter, limit]), [requestFilter, limit]);
  const pagination = useCursorPagination(resetKey);

  const hasTarget = !isEmpty(requestFilter.userId) || !isEmpty(requestFilter.tournamentId);

  const { data, error, isLoading, isValidating } = useSWR(
    hasTarget ? ['getProviderTransactions', limit, pagination.cursor, requestFilter] : null,
    providerTransactionService.getProviderTransactions,
    { keepPreviousData: true, onError: handleProviderTransactionsError, shouldRetryOnError: false }
  );

  const handleChange = useCallback(
    (key: keyof ProviderTransactionFilter) => (value: string) => onChange(key, value),
    [onChange]
  );

  const handleSelectUser = useCallback((userId: string) => onChange('userId', userId), [onChange]);

  // A game belongs to one provider: picking a game sets its provider, changing the provider drops the game.
  const handleSelectSlot = useCallback(
    (slot: Nullable<SlotOption>) =>
      onPatch(slot ? { gameId: slot.identifier, providerName: slot.providerName } : { gameId: '' }),
    [onPatch]
  );

  const handleProviderChange = useCallback(
    (providerName: string) => onPatch({ providerName: providerName as ProviderName | '', gameId: '' }),
    [onPatch]
  );

  const renderContent = () => {
    if (!hasTarget) {
      return <EmptyPlaceholder text="Выберите юзера или турнир" />;
    }

    if (error && !isValidating) {
      return <EmptyPlaceholder text="Не удалось загрузить спины" />;
    }

    if (isLoading && !data) {
      return <Loader />;
    }

    return (
      <ProviderTransactionsTable
        data={data?.result}
        showUserColumn={isEmpty(requestFilter.userId)}
        onSelectUser={handleSelectUser}
      />
    );
  };

  return (
    <PageLayout className={css.providerTransactionsPage} overflow="hidden">
      <PageTitle>Spins</PageTitle>

      <div className={css.providerTransactionsPageContent}>
        <div className={css.filters}>
          <div className="form-group">
            <UserPicker userId={filter.userId} onChange={handleSelectUser} />

            <div className={css.filterItem}>
              <TournamentPicker value={filter.tournamentId} onChange={handleChange('tournamentId')} />
            </div>

            <div className={css.filterItem}>
              <Select
                size="small"
                label="Provider"
                value={filter.providerName}
                options={PROVIDER_OPTIONS}
                onChange={handleProviderChange}
              />
            </div>

            <SlotPicker providerName={filter.providerName} gameId={filter.gameId} onChange={handleSelectSlot} />
          </div>

          <div className="form-group">
            <div className={css.actionFilter}>
              <Select
                size="small"
                label="Action"
                value={filter.actionType}
                options={ACTION_TYPE_OPTIONS}
                onChange={handleChange('actionType')}
              />
            </div>

            <DateTimeFilter label="Date from" value={filter.dateFrom} onChange={handleChange('dateFrom')} />

            <DateTimeFilter label="Date to" value={filter.dateTo} onChange={handleChange('dateTo')} />
          </div>

          <p className={css.hint}>Хранятся спины за последние {TRANSACTIONS_TTL_DAYS} дней</p>
        </div>

        {renderContent()}

        {hasTarget && (
          <CursorPagination
            pagination={pagination}
            nextCursor={data?.nextCursor}
            limit={limit}
            onLimitChange={onLimitChange}
            disabled={isValidating}
          />
        )}
      </div>
    </PageLayout>
  );
};
